import { useEffect, useCallback } from 'react';

/**
 * useFlowchartKeyboard Hook
 * 
 * Handles keyboard shortcuts for flowchart navigation:
 * - "c" - Center on selected node
 * - "f" - Enter focus mode on selected nodes
 * - "Escape" - Exit focus mode
 */
export function useFlowchartKeyboard({
  selectedNodes,
  isFocusMode,
  centerOnTask,
  focusSelectedTasks,
  handleExitFocusMode,
}) {
  // Center on selected node(s)
  const centerOnSelected = useCallback(() => {
    if (selectedNodes.size === 0) return;
    // Center on the first selected node
    const firstSelectedId = Array.from(selectedNodes)[0];
    centerOnTask(firstSelectedId);
  }, [selectedNodes, centerOnTask]);

  // Keyboard shortcuts for flowchart navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't handle if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        return;
      }
      
      // "c" key centers on selected node
      if (e.key === 'c' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        if (selectedNodes.size > 0) {
          e.preventDefault();
          centerOnSelected();
        }
      }
      
      // "f" key enters focus mode on selected nodes
      if (e.key === 'f' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        if (selectedNodes.size > 0 && !isFocusMode) {
          e.preventDefault();
          focusSelectedTasks();
        }
      }
      
      // "Escape" exits focus mode
      if (e.key === 'Escape' && isFocusMode) {
        e.preventDefault();
        handleExitFocusMode();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedNodes, centerOnSelected, isFocusMode, focusSelectedTasks, handleExitFocusMode]);

  return {
    centerOnSelected,
  };
}

export default useFlowchartKeyboard;

